import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Save, HeartHandshake } from "lucide-react";

const WEST_FIELDS = [["institutional", "Institutional tuition"], ["founding_award_max", "Founding award (max)"], ["typical_net", "Typical net (after award)"], ["continuation_award", "Founding Family Continuation"]];
const AFRICA_FIELDS = [["institutional", "Institutional (anchor)"], ["regional_access", "Regional Access Tuition — Africa"], ["founding_award_max", "Founding award (max)"]];

function BandEditor({ title, fields, band, onChange }) {
  const set = (k, v) => onChange({ ...band, [k]: v === "" ? null : Number(v) });
  return (
    <div className="border border-white/10 rounded-sm p-4">
      <div className="text-xs uppercase tracking-wide text-heraldic mb-3">{title}</div>
      <div className="space-y-2">
        {fields.map(([k, label]) => (
          <label key={k} className="flex items-center justify-between text-sm text-paper/60">
            <span>{label}</span>
            <input type="number" value={band?.[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`tf-${title.toLowerCase()}-${k}`} className="w-32 bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1 text-right text-paper" />
          </label>
        ))}
        <label className="flex items-center justify-between text-sm text-paper/60 pt-1">
          <span>Need-based aid available</span>
          <input type="checkbox" checked={!!band?.need_based} onChange={(e) => onChange({ ...band, need_based: e.target.checked })} />
        </label>
      </div>
    </div>
  );
}

export default function AdminTuition() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/tuition").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const setRow = (key, region, band) => setItems(items.map((it) => it.key === key ? { ...it, [region]: band } : it));
  const save = async (row) => {
    try { await api.put(`/admin/tuition/${row.key}`, { west: row.west, africa: row.africa, label_en: row.label_en }); toast.success(`${row.label_en} saved — live on site`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl" data-testid="admin-tuition-page">
        <h1 className="font-serif text-3xl">Tuition & Scholarships</h1>
        <p className="text-paper/50 text-sm mt-1">Edit institutional tuition, awards and Regional Access per programme and region. Saving publishes to the website and feeds Nucleus Pay at the payment stage.</p>
        <div className="mt-6 space-y-4">
          {items.map((row) => (
            <div key={row.key} className="border border-white/10 rounded-sm p-5 bg-white/5" data-testid={`tuition-row-${row.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="font-serif text-xl">{row.label_en}</div>
                <button onClick={() => save(row)} data-testid={`tuition-save-${row.key}`} className="inline-flex items-center gap-1.5 bg-heraldic text-white px-4 py-2 rounded-sm text-sm"><Save className="h-4 w-4" /> Save & publish</button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <BandEditor title="West" fields={WEST_FIELDS} band={row.west} onChange={(b) => setRow(row.key, "west", b)} />
                <BandEditor title="Africa" fields={AFRICA_FIELDS} band={row.africa} onChange={(b) => setRow(row.key, "africa", b)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
